import { auth } from '@/state/auth'
import axios from 'axios'
import type { Branch } from 'functions/_utils/types'
import { produce } from 'immer'
import { create } from 'zustand'

const STORAGE_KEY = 'selectedBranches'

type BranchesState = {
  loading: boolean
  branches: Record<string, Branch[] | undefined>
  selected: Record<string, string | undefined>

  init(): void
  fetch(owner: string, repo: string): Promise<Branch[]>
  getSelected(repoFullName: string, defaultBranch: string): string
  setSelected(repoFullName: string, branch: string): void
  reset(): void
}

export const useBranchesState = create<BranchesState>((set, get) => ({
  loading: false,
  branches: {},
  selected: {},

  init: () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        set({ selected: JSON.parse(stored) })
      }
    }
    catch (e) {
      console.error(e)
    }
  },

  fetch: async (owner, repo) => {
    const repoFullName = `${owner}/${repo}`
    const { branches } = get()

    if (branches[repoFullName]) {
      return branches[repoFullName]
    }

    set({ loading: true })
    try {
      const { data } = await auth(token => (
        axios.get<Branch[]>(`/api/repositories/${owner}/${repo}/branches`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
      ))

      set(produce((s: BranchesState) => {
        s.branches[repoFullName] = data
      }))

      return data
    }
    catch (e) {
      console.error(e)
      return []
    }
    finally {
      set({ loading: false })
    }
  },

  getSelected: (repoFullName, defaultBranch) => {
    const { selected } = get()
    return selected[repoFullName] || defaultBranch
  },

  setSelected: (repoFullName, branch) => {
    set(produce((s: BranchesState) => {
      s.selected[repoFullName] = branch
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s.selected))
    }))
  },

  reset: () => {
    localStorage.removeItem(STORAGE_KEY)
    set({
      loading: false,
      branches: {},
      selected: {},
    })
  },
}))
